import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';

/**
 * Calculate relative luminance of a color (WCAG 2.1)
 * @param {number} r - Red component (0-255)
 * @param {number} g - Green component (0-255)
 * @param {number} b - Blue component (0-255)
 * @returns {number} Relative luminance (0-1)
 */
const getLuminance = (r, g, b) => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const sRGB = c / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : ((sRGB + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calculate contrast ratio between two colors (WCAG 2.1)
 * @param {number} l1 - Luminance of first color
 * @param {number} l2 - Luminance of second color
 * @returns {number} Contrast ratio (1-21)
 */
const getContrastRatio = (l1, l2) => {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Parse a hex color to RGB components
 * @param {string} hex - Hex color string (with or without #)
 * @returns {{ r: number, g: number, b: number }}
 */
const hexToRgb = (hex) => {
  const cleanHex = hex.replace('#', '');
  const bigint = parseInt(cleanHex, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

/**
 * Get an accessible text color for a given background color
 * Implements WCAG 2.1 AA standard (4.5:1 for normal text)
 * @param {string} backgroundColor - Hex color of the background
 * @param {string} lightColor - Light text color option (default: white)
 * @param {string} darkColor - Dark text color option (default: near-black)
 * @returns {string} The accessible text color
 */
export const getReadableTextColor = (
  backgroundColor,
  lightColor = '#ffffff',
  darkColor = '#0f172a'
) => {
  const bg = hexToRgb(backgroundColor);
  const bgLuminance = getLuminance(bg.r, bg.g, bg.b);

  const lightRgb = hexToRgb(lightColor);
  const darkRgb = hexToRgb(darkColor);

  const lightLuminance = getLuminance(lightRgb.r, lightRgb.g, lightRgb.b);
  const darkLuminance = getLuminance(darkRgb.r, darkRgb.g, darkRgb.b);

  const lightContrast = getContrastRatio(bgLuminance, lightLuminance);
  const darkContrast = getContrastRatio(bgLuminance, darkLuminance);

  // Prefer the color with better contrast
  // WCAG AA requires 4.5:1 for normal text
  return lightContrast > darkContrast ? lightColor : darkColor;
};

/**
 * Check if a color combination meets WCAG AA standard
 * @param {string} foreground - Foreground hex color
 * @param {string} background - Background hex color
 * @param {boolean} isLargeText - Whether the text is large (18px+ or 14px+ bold)
 * @returns {boolean}
 */
export const meetsWCAGAA = (foreground, background, isLargeText = false) => {
  const fg = hexToRgb(foreground);
  const bg = hexToRgb(background);
  const fgLuminance = getLuminance(fg.r, fg.g, fg.b);
  const bgLuminance = getLuminance(bg.r, bg.g, bg.b);
  const ratio = getContrastRatio(fgLuminance, bgLuminance);
  const requiredRatio = isLargeText ? 3 : 4.5;
  return ratio >= requiredRatio;
};

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  // Detect system preference
  const getSystemTheme = useCallback(() => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }, []);

  const [systemTheme, setSystemTheme] = useState(getSystemTheme);
  const [userPreference, setUserPreference] = useState(null); // null = follow system

  // The actual theme to use
  const theme = userPreference || systemTheme;
  const isDark = theme === 'dark';

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    // Legacy browsers
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // Update document attribute when theme changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    
    // Also update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        isDark ? '#1a1625' : '#f5f7fb'
      );
    }
  }, [theme, isDark]);

  // Functions to control theme
  const setTheme = useCallback((newTheme) => {
    if (newTheme === 'system') {
      setUserPreference(null);
    } else {
      setUserPreference(newTheme);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setUserPreference((prev) => {
      if (prev === null) {
        return systemTheme === 'dark' ? 'light' : 'dark';
      }
      return prev === 'dark' ? 'light' : 'dark';
    });
  }, [systemTheme]);

  const value = useMemo(
    () => ({
      theme,
      isDark,
      systemTheme,
      userPreference,
      setTheme,
      toggleTheme,
      getReadableTextColor,
      meetsWCAGAA,
    }),
    [theme, isDark, systemTheme, userPreference, setTheme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;

